import React, { useEffect, useState } from "react";
import { Shell } from "../components/Shell";
import { Tabs } from "../components/Tabs";
import { DescargaReporte } from "../components/DescargaReporte";
import { api, ApiError } from "../api";
import { useAuth } from "../auth/AuthContext";

type Cuenta = { id: number; codigo: string; nombre: string; clase: string; naturaleza: string; nivel: number; aceptaMovimiento: boolean; activo: boolean };
type Comprobante = { id: number; tipo: string; numero: number; fecha: string; descripcion: string | null; origen: string; origenTipo: string | null; origenId: number | null; estado: string };
type CentroCosto = { id: number; codigo: string; nombre: string; activo: boolean };
type Periodo = { id: number; anio: number; mes: number; estado: string };
type Retencion = { id: number; concepto: string; terceroTipo: string; terceroNombre: string; documentoOrigen: string; documentoId: number; fecha: string; base: number; tarifaPct: number; valor: number };

const money = (n: number) => `$${Math.round(n).toLocaleString()}`;
const CONCEPTO_LABEL: Record<string, string> = { retefuente_bienes: "Retefuente", reteica: "ReteICA", reteiva: "ReteIVA" };
const ORIGEN_LABEL: Record<string, string> = { manual: "Manual", compras: "Compras", produccion: "Producción", comercial: "Comercial", nomina: "Nómina", cierre: "Cierre" };

export function Contabilidad() {
  const { tienePermiso } = useAuth();
  const puedeCrear = tienePermiso("contabilidad:crear");
  const puedeEditar = tienePermiso("contabilidad:editar");
  const puedeCerrar = tienePermiso("contabilidad:cerrar");

  const [tab, setTab] = useState("comprobantes");
  const [cuentas, setCuentas] = useState<Cuenta[]>([]);
  const [comprobantes, setComprobantes] = useState<Comprobante[]>([]);
  const [centrosCosto, setCentrosCosto] = useState<CentroCosto[]>([]);
  const [periodos, setPeriodos] = useState<Periodo[]>([]);
  const [retenciones, setRetenciones] = useState<Retencion[]>([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const cargarTodo = async () => {
    setCargando(true);
    setError(null);
    try {
      const [cta, comp, cc, per, ret] = await Promise.all([
        api.get("/contabilidad/cuentas"),
        api.get("/contabilidad/comprobantes"),
        api.get("/contabilidad/centros-costo"),
        api.get("/contabilidad/periodos"),
        api.get("/contabilidad/retenciones"),
      ]);
      setCuentas(cta); setComprobantes(comp); setCentrosCosto(cc); setPeriodos(per); setRetenciones(ret);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudo cargar la información contable.");
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => { cargarTodo(); }, []);

  return (
    <Shell titulo="Contabilidad" breadcrumb="Contabilidad General">
      <p className="text-sm text-textmuted mb-4">
        Plan de cuentas, comprobantes de partida doble (generados automáticamente desde Compras y Comercial, o manuales),
        libros, estados financieros y las retenciones que aplican por tercero — la base para declarar impuestos ante la DIAN.
      </p>
      <Tabs
        tabs={[
          { key: "comprobantes", label: "Comprobantes" },
          { key: "cuentas", label: "Plan de cuentas" },
          { key: "libro-diario", label: "Libro diario" },
          { key: "libro-mayor", label: "Libro mayor" },
          { key: "balance-prueba", label: "Balance de prueba" },
          { key: "estados-financieros", label: "Estados financieros" },
          { key: "retenciones", label: "Retenciones" },
          { key: "libros-oficiales", label: "Libros oficiales DIAN" },
          { key: "centros-costo", label: "Centros de costo" },
          { key: "periodos", label: "Períodos" },
          { key: "cuentas-predeterminadas", label: "Cuentas predeterminadas" },
        ]}
        activo={tab} onChange={setTab}
      />
      {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700 mb-4">{error}</div>}
      {cargando ? (
        <p className="text-sm text-textmuted">Cargando...</p>
      ) : (
        <>
          {tab === "comprobantes" && <TabComprobantes comprobantes={comprobantes} cuentas={cuentas} centrosCosto={centrosCosto} puedeCrear={puedeCrear} puedeEditar={puedeEditar} recargar={cargarTodo} />}
          {tab === "cuentas" && <TabCuentas cuentas={cuentas} puedeEditar={puedeEditar} recargar={cargarTodo} />}
          {tab === "libro-diario" && <TabLibroDiario />}
          {tab === "libro-mayor" && <TabLibroMayor cuentas={cuentas} />}
          {tab === "balance-prueba" && <TabBalancePrueba />}
          {tab === "estados-financieros" && <TabEstadosFinancieros />}
          {tab === "retenciones" && <TabRetenciones retenciones={retenciones} />}
          {tab === "libros-oficiales" && <TabLibrosOficiales />}
          {tab === "centros-costo" && <TabCentrosCosto centrosCosto={centrosCosto} puedeEditar={puedeEditar} recargar={cargarTodo} />}
          {tab === "periodos" && <TabPeriodos periodos={periodos} puedeCerrar={puedeCerrar} recargar={cargarTodo} />}
          {tab === "cuentas-predeterminadas" && <TabCuentasPredeterminadas cuentas={cuentas} puedeEditar={puedeEditar} />}
        </>
      )}
    </Shell>
  );
}

// ---------------------------------------------------------------- comprobantes
function TabComprobantes({ comprobantes, cuentas, centrosCosto, puedeCrear, puedeEditar, recargar }: {
  comprobantes: Comprobante[]; cuentas: Cuenta[]; centrosCosto: CentroCosto[]; puedeCrear: boolean; puedeEditar: boolean; recargar: () => void;
}) {
  const [modalAbierto, setModalAbierto] = useState(false);
  const [detalleId, setDetalleId] = useState<number | null>(null);
  const [detalle, setDetalle] = useState<any | null>(null);

  const abrirDetalle = async (id: number) => {
    setDetalleId(id);
    try { setDetalle(await api.get(`/contabilidad/comprobantes/${id}`)); } catch { setDetalle(null); }
  };

  const anular = async (id: number) => {
    try { await api.post(`/contabilidad/comprobantes/${id}/anular`, {}); setDetalleId(null); recargar(); } catch { /* noop */ }
  };

  return (
    <div>
      <div className="flex justify-end mb-3">
        {puedeCrear && <button className="btn-primary" onClick={() => setModalAbierto(true)}>+ Nuevo comprobante</button>}
      </div>
      <div className="card overflow-hidden">
        <table className="w-full">
          <thead><tr className="border-b border-borderc">
            <th className="table-th">Número</th><th className="table-th">Fecha</th><th className="table-th">Tipo</th>
            <th className="table-th">Origen</th><th className="table-th">Descripción</th><th className="table-th">Estado</th><th className="table-th"></th>
          </tr></thead>
          <tbody>
            {comprobantes.map((c) => (
              <tr key={c.id} className="border-b border-borderc last:border-0">
                <td className="table-td font-mono text-xs">{c.numero}</td>
                <td className="table-td text-xs text-textmuted whitespace-nowrap">{c.fecha}</td>
                <td className="table-td capitalize">{c.tipo}</td>
                <td className="table-td"><span className="badge bg-navy/10 text-navy text-[10px]">{ORIGEN_LABEL[c.origen] || c.origen}</span></td>
                <td className="table-td">{c.descripcion}</td>
                <td className="table-td"><span className={`badge ${c.estado === "contabilizado" ? "bg-accent-light text-accent-dark" : "bg-red-50 text-red-700"}`}>{c.estado}</span></td>
                <td className="table-td text-right"><button className="text-accent-dark hover:underline text-xs font-semibold" onClick={() => abrirDetalle(c.id)}>Ver</button></td>
              </tr>
            ))}
            {comprobantes.length === 0 && <tr><td colSpan={7} className="table-td text-center text-textmuted py-8">Sin comprobantes registrados.</td></tr>}
          </tbody>
        </table>
      </div>

      {detalleId !== null && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4 py-8">
          {/* Bug real corregido: este modal no tenía tope de alto ni scroll —
              un comprobante con muchas líneas (p. ej. una liquidación de
              nómina, con salario + auxilio + salud + pensión + fondo de
              solidaridad + varias novedades) crecía más alto que la pantalla
              y, al estar centrado verticalmente, empujaba el botón "✕" de
              arriba y los botones "Cerrar"/"Anular" de abajo fuera del
              viewport por igual — sin scroll dentro del modal para
              alcanzarlos, quedaba visualmente "atascado" (el usuario lo
              describió como si fuera una imagen estática). Se le pone el
              mismo límite max-h + overflow-y-auto que ya usan los demás
              modales largos del sistema (p. ej. el de nueva recepción). */}
          <div className="card w-full max-w-2xl p-6 space-y-4 max-h-[85vh] overflow-y-auto">
            <div className="flex justify-between items-start">
              <h3 className="text-base font-semibold text-slate-800">Comprobante #{detalle?.numero}</h3>
              <button className="text-textmuted hover:text-slate-700 text-sm" onClick={() => setDetalleId(null)}>✕</button>
            </div>
            {detalle ? (
              <>
                <p className="text-xs text-textmuted">{detalle.descripcion} · {detalle.fecha} · {ORIGEN_LABEL[detalle.origen] || detalle.origen}</p>
                <table className="w-full text-sm">
                  <thead><tr className="border-b border-borderc"><th className="table-th">Cuenta</th><th className="table-th">Débito</th><th className="table-th">Crédito</th></tr></thead>
                  <tbody>
                    {detalle.detalle?.map((d: any) => (
                      <tr key={d.id} className="border-b border-borderc last:border-0">
                        <td className="table-td">{d.cuentaCodigo} — {d.cuentaNombre}{d.descripcion ? <span className="block text-[11px] text-textmuted">{d.descripcion}</span> : null}</td>
                        <td className="table-td">{d.debito ? money(d.debito) : "—"}</td>
                        <td className="table-td">{d.credito ? money(d.credito) : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="flex justify-end gap-2 pt-2">
                  {puedeEditar && detalle.estado === "contabilizado" && (
                    <button className="btn-ghost text-red-600" onClick={() => anular(detalle.id)}>Anular</button>
                  )}
                  <button className="btn-primary" onClick={() => setDetalleId(null)}>Cerrar</button>
                </div>
              </>
            ) : <p className="text-sm text-textmuted">Cargando...</p>}
          </div>
        </div>
      )}

      {modalAbierto && (
        <ModalComprobanteManual cuentas={cuentas} centrosCosto={centrosCosto} onClose={() => setModalAbierto(false)} onCreado={() => { setModalAbierto(false); recargar(); }} />
      )}
    </div>
  );
}

function ModalComprobanteManual({ cuentas, centrosCosto, onClose, onCreado }: {
  cuentas: Cuenta[]; centrosCosto: CentroCosto[]; onClose: () => void; onCreado: () => void;
}) {
  const [tipo, setTipo] = useState("diario");
  const [fecha, setFecha] = useState(new Date().toISOString().slice(0, 10));
  const [descripcion, setDescripcion] = useState("");
  const [lineas, setLineas] = useState([
    { cuentaId: "", centroCostoId: "", debito: "", credito: "", descripcion: "" },
    { cuentaId: "", centroCostoId: "", debito: "", credito: "", descripcion: "" },
  ]);
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const cuentasMovimiento = cuentas.filter((c) => c.aceptaMovimiento && c.activo);
  const totalDebito = lineas.reduce((s, l) => s + (Number(l.debito) || 0), 0);
  const totalCredito = lineas.reduce((s, l) => s + (Number(l.credito) || 0), 0);
  const cuadra = totalDebito === totalCredito && totalDebito > 0;

  const actualizarLinea = (i: number, campo: string, valor: string) => {
    setLineas(lineas.map((l, idx) => (idx === i ? { ...l, [campo]: valor } : l)));
  };

  const agregarLinea = () => setLineas([...lineas, { cuentaId: "", centroCostoId: "", debito: "", credito: "", descripcion: "" }]);
  const quitarLinea = (i: number) => setLineas(lineas.filter((_, idx) => idx !== i));

  const crear = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!cuadra) { setError("La partida debe cuadrar: la suma de débitos debe ser igual a la suma de créditos."); return; }
    setEnviando(true);
    try {
      await api.post("/contabilidad/comprobantes", {
        tipo, fecha, descripcion,
        lineas: lineas.filter((l) => l.cuentaId).map((l) => ({
          cuentaId: Number(l.cuentaId), centroCostoId: l.centroCostoId ? Number(l.centroCostoId) : null,
          debito: Number(l.debito) || 0, credito: Number(l.credito) || 0, descripcion: l.descripcion || null,
        })),
      });
      onCreado();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudo contabilizar el comprobante.");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
      <form onSubmit={crear} className="card w-full max-w-2xl p-6 space-y-4 max-h-[90vh] overflow-y-auto">
        <h3 className="text-base font-semibold text-slate-800">Nuevo comprobante manual</h3>
        <div className="grid grid-cols-3 gap-3">
          <div><label className="block text-xs font-semibold text-slate-600 mb-1">Tipo</label>
            <select className="input-field" value={tipo} onChange={(e) => setTipo(e.target.value)}>
              <option value="diario">Diario</option><option value="ingreso">Ingreso</option>
              <option value="egreso">Egreso</option><option value="ajuste">Ajuste</option>
            </select></div>
          <div><label className="block text-xs font-semibold text-slate-600 mb-1">Fecha</label>
            <input className="input-field" type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} required /></div>
          <div><label className="block text-xs font-semibold text-slate-600 mb-1">Descripción</label>
            <input className="input-field" value={descripcion} onChange={(e) => setDescripcion(e.target.value)} /></div>
        </div>

        <div>
          <div className="flex justify-between items-center mb-2">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Líneas</p>
            <button type="button" className="text-accent-dark text-xs font-semibold hover:underline" onClick={agregarLinea}>+ Agregar línea</button>
          </div>
          <div className="space-y-2">
            {lineas.map((l, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-center">
                <select className="input-field col-span-4" value={l.cuentaId} onChange={(e) => actualizarLinea(i, "cuentaId", e.target.value)}>
                  <option value="">Cuenta...</option>
                  {cuentasMovimiento.map((c) => <option key={c.id} value={c.id}>{c.codigo} — {c.nombre}</option>)}
                </select>
                <select className="input-field col-span-2" value={l.centroCostoId} onChange={(e) => actualizarLinea(i, "centroCostoId", e.target.value)}>
                  <option value="">C. costo</option>
                  {centrosCosto.map((cc) => <option key={cc.id} value={cc.id}>{cc.codigo}</option>)}
                </select>
                <input className="input-field col-span-2" type="number" step="0.01" placeholder="Débito" value={l.debito} onChange={(e) => actualizarLinea(i, "debito", e.target.value)} />
                <input className="input-field col-span-2" type="number" step="0.01" placeholder="Crédito" value={l.credito} onChange={(e) => actualizarLinea(i, "credito", e.target.value)} />
                <input className="input-field col-span-1" placeholder="Nota" value={l.descripcion} onChange={(e) => actualizarLinea(i, "descripcion", e.target.value)} />
                <button type="button" className="col-span-1 text-red-500 text-xs" onClick={() => quitarLinea(i)} disabled={lineas.length <= 2}>✕</button>
              </div>
            ))}
          </div>
        </div>

        <div className={`rounded-md px-3 py-2 text-sm flex justify-between ${cuadra ? "bg-accent-light text-accent-dark" : "bg-amber-50 text-amber-700"}`}>
          <span>Débitos: {money(totalDebito)}</span><span>Créditos: {money(totalCredito)}</span>
          <span>{cuadra ? "✓ Cuadra" : "No cuadra"}</span>
        </div>

        {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-ghost" onClick={onClose}>Cancelar</button>
          <button type="submit" className="btn-primary" disabled={enviando || !cuadra}>{enviando ? "Contabilizando..." : "Contabilizar"}</button>
        </div>
      </form>
    </div>
  );
}

// ---------------------------------------------------------------- plan de cuentas
function TabCuentas({ cuentas, puedeEditar, recargar }: { cuentas: Cuenta[]; puedeEditar: boolean; recargar: () => void }) {
  const [modalAbierto, setModalAbierto] = useState(false);
  const [codigo, setCodigo] = useState("");
  const [nombre, setNombre] = useState("");
  const [clase, setClase] = useState("activo");
  const [naturaleza, setNaturaleza] = useState("debito");
  const [cuentaPadreId, setCuentaPadreId] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const crear = async (e: React.FormEvent) => {
    e.preventDefault();
    setEnviando(true);
    setError(null);
    try {
      await api.post("/contabilidad/cuentas", {
        codigo, nombre, clase, naturaleza, cuentaPadreId: cuentaPadreId ? Number(cuentaPadreId) : null,
        nivel: cuentaPadreId ? (cuentas.find((c) => c.id === Number(cuentaPadreId))?.nivel || 1) + 1 : 1,
        aceptaMovimiento: true,
      });
      setModalAbierto(false);
      setCodigo(""); setNombre(""); setCuentaPadreId("");
      recargar();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudo crear la cuenta.");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div>
      <div className="flex justify-end mb-3">
        {puedeEditar && <button className="btn-primary" onClick={() => setModalAbierto(true)}>+ Nueva cuenta</button>}
      </div>
      <div className="card overflow-hidden">
        <table className="w-full">
          <thead><tr className="border-b border-borderc"><th className="table-th">Código</th><th className="table-th">Nombre</th><th className="table-th">Clase</th><th className="table-th">Naturaleza</th><th className="table-th">Movimiento</th></tr></thead>
          <tbody>
            {cuentas.map((c) => (
              <tr key={c.id} className="border-b border-borderc last:border-0">
                <td className="table-td font-mono text-xs" style={{ paddingLeft: `${(c.nivel - 1) * 16 + 8}px` }}>{c.codigo}</td>
                <td className={`table-td ${c.nivel <= 2 ? "font-semibold text-slate-800" : ""}`}>{c.nombre}</td>
                <td className="table-td capitalize">{c.clase}</td>
                <td className="table-td capitalize">{c.naturaleza}</td>
                <td className="table-td">{c.aceptaMovimiento ? <span className="badge bg-accent-light text-accent-dark">movimiento</span> : <span className="badge bg-slate-100 text-slate-500">agrupación</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {modalAbierto && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
          <form onSubmit={crear} className="card w-full max-w-md p-6 space-y-4">
            <h3 className="text-base font-semibold text-slate-800">Nueva cuenta contable</h3>
            <div><label className="block text-xs font-semibold text-slate-600 mb-1">Código</label>
              <input className="input-field" value={codigo} onChange={(e) => setCodigo(e.target.value)} required /></div>
            <div><label className="block text-xs font-semibold text-slate-600 mb-1">Nombre</label>
              <input className="input-field" value={nombre} onChange={(e) => setNombre(e.target.value)} required /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="block text-xs font-semibold text-slate-600 mb-1">Clase</label>
                <select className="input-field" value={clase} onChange={(e) => setClase(e.target.value)}>
                  <option value="activo">Activo</option><option value="pasivo">Pasivo</option>
                  <option value="patrimonio">Patrimonio</option><option value="ingreso">Ingreso</option>
                  <option value="gasto">Gasto</option><option value="costo">Costo</option>
                </select></div>
              <div><label className="block text-xs font-semibold text-slate-600 mb-1">Naturaleza</label>
                <select className="input-field" value={naturaleza} onChange={(e) => setNaturaleza(e.target.value)}>
                  <option value="debito">Débito</option><option value="credito">Crédito</option>
                </select></div>
            </div>
            <div><label className="block text-xs font-semibold text-slate-600 mb-1">Cuenta padre (opcional)</label>
              <select className="input-field" value={cuentaPadreId} onChange={(e) => setCuentaPadreId(e.target.value)}>
                <option value="">Sin padre (nivel 1)</option>
                {cuentas.map((c) => <option key={c.id} value={c.id}>{c.codigo} — {c.nombre}</option>)}
              </select></div>
            {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</div>}
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" className="btn-ghost" onClick={() => setModalAbierto(false)}>Cancelar</button>
              <button type="submit" className="btn-primary" disabled={enviando}>{enviando ? "Guardando..." : "Crear"}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- libro diario
function TabLibroDiario() {
  const [movimientos, setMovimientos] = useState<any[]>([]);
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    api.get("/contabilidad/libro-diario").then(setMovimientos).finally(() => setCargando(false));
  }, []);

  if (cargando) return <p className="text-sm text-textmuted">Cargando...</p>;
  return (
    <div>
      <div className="flex justify-end mb-3">
        <DescargaReporte endpoint="/contabilidad/libro-diario/exportar" nombreBase="libro_diario" label="Descargar libro diario" />
      </div>
      <div className="card overflow-hidden">
        <table className="w-full">
          <thead><tr className="border-b border-borderc"><th className="table-th">Fecha</th><th className="table-th">No.</th><th className="table-th">Cuenta</th><th className="table-th">Débito</th><th className="table-th">Crédito</th><th className="table-th">Descripción</th></tr></thead>
          <tbody>
            {movimientos.map((m, i) => (
              <tr key={i} className="border-b border-borderc last:border-0">
                <td className="table-td text-xs text-textmuted whitespace-nowrap">{m.fecha}</td>
                <td className="table-td font-mono text-xs">{m.numero}</td>
                <td className="table-td">{m.cuentaCodigo} — {m.cuentaNombre}</td>
                <td className="table-td">{m.debito ? money(m.debito) : "—"}</td>
                <td className="table-td">{m.credito ? money(m.credito) : "—"}</td>
                <td className="table-td text-xs text-textmuted">{m.descripcion || m.comprobanteDescripcion}</td>
              </tr>
            ))}
            {movimientos.length === 0 && <tr><td colSpan={6} className="table-td text-center text-textmuted py-8">Sin movimientos.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- libro mayor
function TabLibroMayor({ cuentas }: { cuentas: Cuenta[] }) {
  const [cuentaId, setCuentaId] = useState("");
  const [movimientos, setMovimientos] = useState<any[]>([]);
  const cuentasMovimiento = cuentas.filter((c) => c.aceptaMovimiento);

  useEffect(() => {
    if (!cuentaId) { setMovimientos([]); return; }
    api.get(`/contabilidad/libro-mayor?cuentaId=${cuentaId}`).then(setMovimientos).catch(() => setMovimientos([]));
  }, [cuentaId]);

  const cuentaSeleccionada = cuentasMovimiento.find((c) => String(c.id) === cuentaId);

  return (
    <div>
      <div className="mb-3 flex justify-between items-end flex-wrap gap-3">
        <div className="max-w-sm w-full">
          <select className="input-field" value={cuentaId} onChange={(e) => setCuentaId(e.target.value)}>
            <option value="">Seleccione una cuenta...</option>
            {cuentasMovimiento.map((c) => <option key={c.id} value={c.id}>{c.codigo} — {c.nombre}</option>)}
          </select>
        </div>
        {cuentaId && (
          <DescargaReporte
            endpoint="/contabilidad/libro-mayor/exportar" params={{ cuentaId }}
            nombreBase={`libro_mayor_${cuentaSeleccionada?.codigo || cuentaId}`} label="Descargar libro mayor"
          />
        )}
      </div>
      <div className="card overflow-hidden">
        <table className="w-full">
          <thead><tr className="border-b border-borderc"><th className="table-th">Fecha</th><th className="table-th">No.</th><th className="table-th">Débito</th><th className="table-th">Crédito</th><th className="table-th">Saldo</th><th className="table-th">Descripción</th></tr></thead>
          <tbody>
            {movimientos.map((m, i) => (
              <tr key={i} className="border-b border-borderc last:border-0">
                <td className="table-td text-xs text-textmuted whitespace-nowrap">{m.fecha}</td>
                <td className="table-td font-mono text-xs">{m.numero}</td>
                <td className="table-td">{m.debito ? money(m.debito) : "—"}</td>
                <td className="table-td">{m.credito ? money(m.credito) : "—"}</td>
                <td className="table-td font-semibold">{money(m.saldoAcumulado)}</td>
                <td className="table-td text-xs text-textmuted">{m.descripcion || m.comprobanteDescripcion}</td>
              </tr>
            ))}
            {cuentaId && movimientos.length === 0 && <tr><td colSpan={6} className="table-td text-center text-textmuted py-8">Sin movimientos para esta cuenta.</td></tr>}
            {!cuentaId && <tr><td colSpan={6} className="table-td text-center text-textmuted py-8">Seleccione una cuenta para ver su mayor.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- balance de prueba
function TabBalancePrueba() {
  const [datos, setDatos] = useState<any | null>(null);

  useEffect(() => { api.get("/contabilidad/balance-prueba").then(setDatos); }, []);
  if (!datos) return <p className="text-sm text-textmuted">Cargando...</p>;

  return (
    <div>
      <div className="flex justify-end mb-3">
        <DescargaReporte endpoint="/contabilidad/balance-prueba/exportar" params={{ hasta: datos.corte }} nombreBase={`balance_prueba_${datos.corte}`} label="Descargar balance de prueba" />
      </div>
      <div className={`rounded-md px-4 py-3 mb-4 text-sm font-semibold flex justify-between ${datos.cuadra ? "bg-accent-light text-accent-dark" : "bg-red-50 text-red-700"}`}>
        <span>Corte: {datos.corte}</span>
        <span>Débitos: {money(datos.totalDebito)} · Créditos: {money(datos.totalCredito)}</span>
        <span>{datos.cuadra ? "✓ Cuadra" : "✗ No cuadra"}</span>
      </div>
      <div className="card overflow-hidden">
        <table className="w-full">
          <thead><tr className="border-b border-borderc"><th className="table-th">Cuenta</th><th className="table-th">Clase</th><th className="table-th">Total débito</th><th className="table-th">Total crédito</th><th className="table-th">Saldo</th></tr></thead>
          <tbody>
            {datos.cuentas.map((c: any) => (
              <tr key={c.cuentaId} className="border-b border-borderc last:border-0">
                <td className="table-td">{c.codigo} — {c.nombre}</td>
                <td className="table-td capitalize">{c.clase}</td>
                <td className="table-td">{money(c.totalDebito)}</td>
                <td className="table-td">{money(c.totalCredito)}</td>
                <td className="table-td font-semibold">{money(c.saldo)}</td>
              </tr>
            ))}
            {datos.cuentas.length === 0 && <tr><td colSpan={5} className="table-td text-center text-textmuted py-8">Sin movimientos contables aún.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- estados financieros
function TabEstadosFinancieros() {
  const [er, setEr] = useState<any | null>(null);
  const [bg, setBg] = useState<any | null>(null);
  const [fe, setFe] = useState<any | null>(null);
  const [ecp, setEcp] = useState<any | null>(null);

  useEffect(() => {
    api.get("/contabilidad/estado-resultados").then(setEr);
    api.get("/contabilidad/balance-general").then(setBg);
    api.get("/contabilidad/estado-flujo-efectivo").then(setFe);
    api.get("/contabilidad/estado-cambios-patrimonio").then(setEcp);
  }, []);

  if (!er || !bg || !fe || !ecp) return <p className="text-sm text-textmuted">Cargando...</p>;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="card p-5">
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-sm font-semibold text-slate-800">Estado de resultados</h3>
          <DescargaReporte endpoint="/contabilidad/estado-resultados/exportar" params={{ hasta: er.corte }} nombreBase={`estado_resultados_${er.corte}`} label="Descargar" />
        </div>
        <div className="space-y-1 text-sm">
          <Linea label="Ingresos operacionales" valor={er.totalIngresos} />
          <Linea label="Costo de ventas" valor={-er.totalCostos} />
          <Linea label="Utilidad bruta" valor={er.utilidadBruta} negrita />
          <Linea label="Gastos" valor={-er.totalGastos} />
          <Linea label="Utilidad neta" valor={er.utilidadNeta} negrita destacado />
        </div>
      </div>
      <div className="card p-5">
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-sm font-semibold text-slate-800">Balance general</h3>
          <DescargaReporte endpoint="/contabilidad/balance-general/exportar" params={{ hasta: bg.corte }} nombreBase={`balance_general_${bg.corte}`} label="Descargar" />
        </div>
        <div className="space-y-1 text-sm">
          <Linea label="Total activo" valor={bg.totalActivo} negrita />
          <Linea label="Total pasivo" valor={bg.totalPasivo} />
          <Linea label="Total patrimonio (incl. utilidad del ejercicio)" valor={bg.totalPatrimonio} />
          <div className={`mt-2 rounded-md px-3 py-2 text-xs font-semibold ${bg.cuadra ? "bg-accent-light text-accent-dark" : "bg-red-50 text-red-700"}`}>
            {bg.cuadra ? "✓ Activo = Pasivo + Patrimonio" : "✗ El balance no cuadra"}
          </div>
        </div>
      </div>
      <div className="card p-5">
        <div className="flex justify-between items-start mb-1">
          <h3 className="text-sm font-semibold text-slate-800">Estado de flujos de efectivo</h3>
          <DescargaReporte endpoint="/contabilidad/estado-flujo-efectivo/exportar" params={{ desde: fe.desde, hasta: fe.hasta }} nombreBase={`flujo_efectivo_${fe.desde}_${fe.hasta}`} label="Descargar" />
        </div>
        <p className="text-[11px] text-textmuted mb-3">Método indirecto · {fe.desde} a {fe.hasta}</p>
        <div className="space-y-1 text-sm">
          <Linea label="Utilidad neta del período" valor={fe.utilidadNeta} />
          <Linea label="+ Depreciación del período" valor={fe.depreciacionPeriodo} />
          <Linea label="+ Pérdida en baja de activos" valor={fe.perdidaBajaActivosPeriodo} />
          <Linea label="+/- Variación capital de trabajo" valor={fe.variacionCapitalTrabajo} />
          <Linea label="Flujo neto de operación" valor={fe.flujoOperacion} negrita />
          <Linea label="Flujo neto de inversión" valor={fe.flujoInversion} negrita />
          <Linea label="Flujo neto de financiación" valor={fe.flujoFinanciacion} negrita />
          <Linea label="Aumento (disminución) neta de efectivo" valor={fe.aumentoNetoEfectivo} negrita destacado />
          <div className="flex justify-between text-xs text-textmuted pt-1">
            <span>Efectivo inicial: {money(fe.efectivoInicio)}</span>
            <span>Efectivo final: {money(fe.efectivoFin)}</span>
          </div>
          <div className={`mt-2 rounded-md px-3 py-2 text-xs font-semibold ${fe.cuadra ? "bg-accent-light text-accent-dark" : "bg-red-50 text-red-700"}`}>
            {fe.cuadra ? "✓ Cuadra contra el saldo real de efectivo" : "✗ No cuadra — revisar plan de cuentas"}
          </div>
        </div>
      </div>
      <div className="card p-5">
        <div className="flex justify-between items-start mb-1">
          <h3 className="text-sm font-semibold text-slate-800">Estado de cambios en el patrimonio</h3>
          <DescargaReporte endpoint="/contabilidad/estado-cambios-patrimonio/exportar" params={{ desde: ecp.desde, hasta: ecp.hasta }} nombreBase={`cambios_patrimonio_${ecp.desde}_${ecp.hasta}`} label="Descargar" />
        </div>
        <p className="text-[11px] text-textmuted mb-3">{ecp.desde} a {ecp.hasta}</p>
        <table className="w-full text-sm">
          <thead><tr className="border-b border-borderc">
            <th className="table-th">Cuenta</th><th className="table-th">Saldo inicial</th><th className="table-th">Movimiento</th><th className="table-th">Saldo final</th>
          </tr></thead>
          <tbody>
            {ecp.cuentas.map((c: any, i: number) => (
              <tr key={i} className="border-b border-borderc last:border-0">
                <td className="table-td">{c.codigo ? `${c.codigo} — ${c.nombre}` : <em className="text-textmuted">{c.nombre}</em>}</td>
                <td className="table-td">{money(c.saldoInicial)}</td>
                <td className="table-td">{money(c.movimiento)}</td>
                <td className="table-td font-semibold">{money(c.saldoFinal)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="mt-2 flex justify-between text-sm font-semibold text-accent-dark border-t border-borderc pt-2">
          <span>Total patrimonio</span><span>{money(ecp.totalPatrimonioInicial)} → {money(ecp.totalPatrimonioFinal)}</span>
        </div>
      </div>
    </div>
  );
}

function Linea({ label, valor, negrita, destacado }: { label: string; valor: number; negrita?: boolean; destacado?: boolean }) {
  return (
    <div className={`flex justify-between py-1 ${negrita ? "font-semibold" : ""} ${destacado ? "text-accent-dark border-t border-borderc pt-2 mt-1" : "text-slate-700"}`}>
      <span>{label}</span><span>{money(valor)}</span>
    </div>
  );
}

// ---------------------------------------------------------------- retenciones
function TabRetenciones({ retenciones }: { retenciones: Retencion[] }) {
  return (
    <div className="space-y-3">
      <DescargaReporte endpoint="/contabilidad/retenciones/exportar" nombreBase="retenciones" label="Descargar retenciones" />
      <div className="card overflow-hidden">
      <table className="w-full">
        <thead><tr className="border-b border-borderc"><th className="table-th">Fecha</th><th className="table-th">Concepto</th><th className="table-th">Tercero</th><th className="table-th">Documento</th><th className="table-th">Base</th><th className="table-th">Tarifa</th><th className="table-th">Valor</th></tr></thead>
        <tbody>
          {retenciones.map((r) => (
            <tr key={r.id} className="border-b border-borderc last:border-0">
              <td className="table-td text-xs text-textmuted whitespace-nowrap">{r.fecha}</td>
              <td className="table-td"><span className="badge bg-amber-50 text-amber-700">{CONCEPTO_LABEL[r.concepto] || r.concepto}</span></td>
              <td className="table-td">{r.terceroNombre} <span className="text-[10px] text-textmuted">({r.terceroTipo})</span></td>
              <td className="table-td text-xs text-textmuted">{r.documentoOrigen} #{r.documentoId}</td>
              <td className="table-td">{money(r.base)}</td>
              <td className="table-td">{r.tarifaPct}%</td>
              <td className="table-td font-semibold">{money(r.valor)}</td>
            </tr>
          ))}
          {retenciones.length === 0 && <tr><td colSpan={7} className="table-td text-center text-textmuted py-8">Sin retenciones aplicadas aún.</td></tr>}
        </tbody>
      </table>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- libros oficiales DIAN
// (Ampliación tras comparar con Siigo Nube — pestaña "Libros oficiales".) Reconstruyen
// base/IVA/retenciones a partir de los comprobantes ya contabilizados de cada documento,
// así que siempre cuadran con la contabilidad — ver contabilidad_utils.py.
function TabLibrosOficiales() {
  const anio = new Date().getFullYear();
  const [desde, setDesde] = useState(`${anio}-01-01`);
  const [hasta, setHasta] = useState(new Date().toISOString().slice(0, 10));
  const [sub, setSub] = useState("ventas");
  const [ventas, setVentas] = useState<any | null>(null);
  const [compras, setCompras] = useState<any | null>(null);
  const [inventariosBalance, setInventariosBalance] = useState<any | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const cargar = async () => {
    setCargando(true);
    setError(null);
    try {
      const [v, c, ib] = await Promise.all([
        api.get(`/contabilidad/libro-ventas?desde=${desde}&hasta=${hasta}`),
        api.get(`/contabilidad/libro-compras?desde=${desde}&hasta=${hasta}`),
        api.get(`/contabilidad/libro-inventarios-balance?hasta=${hasta}`),
      ]);
      setVentas(v); setCompras(c); setInventariosBalance(ib);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudieron cargar los libros oficiales.");
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => { cargar(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div>
      <p className="text-xs text-textmuted mb-3">
        Libro oficial de ventas, libro oficial de compras y libro de inventarios y balances — los libros que exige la DIAN,
        derivados directamente de la contabilidad ya generada (sin volver a calcular impuestos).
      </p>
      <div className="card p-4 mb-4 flex flex-wrap items-end gap-3">
        <div><label className="block text-xs font-semibold text-slate-600 mb-1">Desde</label>
          <input className="input-field" type="date" value={desde} onChange={(e) => setDesde(e.target.value)} /></div>
        <div><label className="block text-xs font-semibold text-slate-600 mb-1">Hasta</label>
          <input className="input-field" type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} /></div>
        <button className="btn-primary" onClick={cargar} disabled={cargando}>{cargando ? "Actualizando..." : "Actualizar"}</button>
      </div>
      {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700 mb-4">{error}</div>}

      <Tabs
        tabs={[
          { key: "ventas", label: "Libro de ventas" },
          { key: "compras", label: "Libro de compras" },
          { key: "inventarios-balance", label: "Inventarios y balance" },
        ]}
        activo={sub} onChange={setSub}
      />

      {cargando ? (
        <p className="text-sm text-textmuted mt-3">Cargando...</p>
      ) : (
        <div className="mt-3">
          {sub === "ventas" && ventas && (
            <div>
              <div className="flex justify-end mb-3">
                <DescargaReporte endpoint="/contabilidad/libro-ventas/exportar" params={{ desde, hasta }} nombreBase={`libro_ventas_${desde}_${hasta}`} label="Descargar libro de ventas" />
              </div>
              {ventas.aviso && <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-700 mb-3">{ventas.aviso}</div>}
              <div className="card overflow-hidden">
                <table className="w-full">
                  <thead><tr className="border-b border-borderc">
                    <th className="table-th">Factura</th><th className="table-th">Fecha</th><th className="table-th">Cliente</th>
                    <th className="table-th">NIT</th><th className="table-th">Base</th><th className="table-th">IVA</th>
                    <th className="table-th">Retenciones</th><th className="table-th">Neto</th>
                  </tr></thead>
                  <tbody>
                    {ventas.lineas.map((l: any) => (
                      <tr key={l.facturaId} className="border-b border-borderc last:border-0">
                        <td className="table-td font-mono text-xs font-semibold">{l.numero}</td>
                        <td className="table-td text-xs text-textmuted whitespace-nowrap">{l.fecha}</td>
                        <td className="table-td">{l.clienteNombre}</td>
                        <td className="table-td text-xs">{l.clienteNit}</td>
                        <td className="table-td">{money(l.base)}</td>
                        <td className="table-td">{money(l.iva)}</td>
                        <td className="table-td">{money(l.retenciones)}</td>
                        <td className="table-td font-semibold">{money(l.neto)}</td>
                      </tr>
                    ))}
                    {ventas.lineas.length === 0 && <tr><td colSpan={8} className="table-td text-center text-textmuted py-8">Sin facturas de venta en el período.</td></tr>}
                  </tbody>
                  {ventas.lineas.length > 0 && (
                    <tfoot><tr className="border-t-2 border-borderc font-semibold text-sm">
                      <td className="table-td" colSpan={4}>Totales</td>
                      <td className="table-td">{money(ventas.totalBase)}</td>
                      <td className="table-td">{money(ventas.totalIva)}</td>
                      <td className="table-td">{money(ventas.totalRetenciones)}</td>
                      <td className="table-td">{money(ventas.totalNeto)}</td>
                    </tr></tfoot>
                  )}
                </table>
              </div>
            </div>
          )}

          {sub === "compras" && compras && (
            <div>
              <div className="flex justify-end mb-3">
                <DescargaReporte endpoint="/contabilidad/libro-compras/exportar" params={{ desde, hasta }} nombreBase={`libro_compras_${desde}_${hasta}`} label="Descargar libro de compras" />
              </div>
              <div className="card overflow-hidden">
                <table className="w-full">
                <thead><tr className="border-b border-borderc">
                  <th className="table-th">Documento</th><th className="table-th">Tipo</th><th className="table-th">Fecha</th><th className="table-th">Proveedor</th>
                  <th className="table-th">NIT</th><th className="table-th">Base</th><th className="table-th">IVA</th>
                  <th className="table-th">Retenciones</th><th className="table-th">Neto</th>
                </tr></thead>
                <tbody>
                  {compras.lineas.map((l: any) => (
                    <tr key={`${l.tipoDocumento}-${l.documentoId}`} className="border-b border-borderc last:border-0">
                      <td className="table-td font-mono text-xs font-semibold">{l.numero}</td>
                      <td className="table-td"><span className="badge bg-navy/10 text-navy text-[10px]">{l.tipoDocumento === "recepcion" ? "Recepción" : "Doc. soporte"}</span></td>
                      <td className="table-td text-xs text-textmuted whitespace-nowrap">{l.fecha}</td>
                      <td className="table-td">{l.proveedorNombre}</td>
                      <td className="table-td text-xs">{l.proveedorNit}</td>
                      <td className="table-td">{money(l.base)}</td>
                      <td className="table-td">{money(l.iva)}</td>
                      <td className="table-td">{money(l.retenciones)}</td>
                      <td className="table-td font-semibold">{money(l.neto)}</td>
                    </tr>
                  ))}
                  {compras.lineas.length === 0 && <tr><td colSpan={9} className="table-td text-center text-textmuted py-8">Sin compras en el período.</td></tr>}
                </tbody>
                {compras.lineas.length > 0 && (
                  <tfoot><tr className="border-t-2 border-borderc font-semibold text-sm">
                    <td className="table-td" colSpan={5}>Totales</td>
                    <td className="table-td">{money(compras.totalBase)}</td>
                    <td className="table-td">{money(compras.totalIva)}</td>
                    <td className="table-td">{money(compras.totalRetenciones)}</td>
                    <td className="table-td">{money(compras.totalNeto)}</td>
                  </tr></tfoot>
                )}
                </table>
              </div>
            </div>
          )}

          {sub === "inventarios-balance" && inventariosBalance && (
            <div className="space-y-6">
              <div className="flex justify-end">
                <DescargaReporte endpoint="/contabilidad/libro-inventarios-balance/exportar" params={{ hasta }} nombreBase={`inventarios_balance_${hasta}`} label="Descargar libro de inventarios y balances" />
              </div>
              <div className={`rounded-md px-4 py-3 text-sm font-semibold flex justify-between ${inventariosBalance.cuadra ? "bg-accent-light text-accent-dark" : "bg-red-50 text-red-700"}`}>
                <span>Corte: {inventariosBalance.corte}</span>
                <span>Activo: {money(inventariosBalance.totalActivo)} · Pasivo + Patrimonio: {money(inventariosBalance.totalPasivo + inventariosBalance.totalPatrimonio)}</span>
                <span>{inventariosBalance.cuadra ? "✓ Cuadra" : "✗ No cuadra"}</span>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="card p-4">
                  <h4 className="text-sm font-semibold text-slate-800 mb-2">Materia prima disponible</h4>
                  <table className="w-full text-sm mb-2">
                    <thead><tr className="border-b border-borderc"><th className="table-th">Bodega</th><th className="table-th">Especie</th><th className="table-th">Kg disp.</th></tr></thead>
                    <tbody>
                      {inventariosBalance.inventarioMateriaPrima.map((r: any, i: number) => (
                        <tr key={i} className="border-b border-borderc last:border-0">
                          <td className="table-td">{r.bodegaNombre}</td><td className="table-td">{r.especieNombre}</td><td className="table-td">{r.kgDisponible.toLocaleString()} kg</td>
                        </tr>
                      ))}
                      {inventariosBalance.inventarioMateriaPrima.length === 0 && <tr><td colSpan={3} className="table-td text-center text-textmuted py-4">Sin existencias.</td></tr>}
                    </tbody>
                  </table>
                  <div className="flex justify-between text-xs font-semibold text-accent-dark border-t border-borderc pt-2">
                    <span>Total: {inventariosBalance.totalKgMateriaPrima.toLocaleString()} kg</span>
                    <span>Valor en libros: {money(inventariosBalance.valorInventarioMateriaPrima)}</span>
                  </div>
                </div>
                <div className="card p-4">
                  <h4 className="text-sm font-semibold text-slate-800 mb-2">Producto terminado disponible</h4>
                  <table className="w-full text-sm mb-2">
                    <thead><tr className="border-b border-borderc"><th className="table-th">Bodega</th><th className="table-th">Producto</th><th className="table-th">Lote</th><th className="table-th">Cant.</th></tr></thead>
                    <tbody>
                      {inventariosBalance.inventarioProductoTerminado.map((r: any, i: number) => (
                        <tr key={i} className="border-b border-borderc last:border-0">
                          <td className="table-td">{r.bodegaNombre}</td><td className="table-td">{r.producto}</td>
                          <td className="table-td font-mono text-xs">{r.codigoLote || <span className="font-sans text-textmuted">Compra directa</span>}</td><td className="table-td">{r.cantidad.toLocaleString()}</td>
                        </tr>
                      ))}
                      {inventariosBalance.inventarioProductoTerminado.length === 0 && <tr><td colSpan={4} className="table-td text-center text-textmuted py-4">Sin existencias.</td></tr>}
                    </tbody>
                  </table>
                  <div className="flex justify-between text-xs font-semibold text-accent-dark border-t border-borderc pt-2">
                    <span>Total: {inventariosBalance.totalUnidadesProductoTerminado.toLocaleString()} unid.</span>
                    <span>Valor en libros: {money(inventariosBalance.valorInventarioProductoTerminado)}</span>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {[
                  { titulo: "Activo", cuentas: inventariosBalance.activo, total: inventariosBalance.totalActivo },
                  { titulo: "Pasivo", cuentas: inventariosBalance.pasivo, total: inventariosBalance.totalPasivo },
                  { titulo: "Patrimonio (incl. utilidad del ejercicio)", cuentas: inventariosBalance.patrimonio, total: inventariosBalance.totalPatrimonio },
                ].map((grupo) => (
                  <div key={grupo.titulo} className="card p-4">
                    <h4 className="text-sm font-semibold text-slate-800 mb-2">{grupo.titulo}</h4>
                    <div className="space-y-1 text-xs">
                      {grupo.cuentas.map((c: any) => (
                        <div key={c.cuentaId} className="flex justify-between text-slate-700">
                          <span>{c.codigo} — {c.nombre}</span><span>{money(c.saldo)}</span>
                        </div>
                      ))}
                    </div>
                    <div className="flex justify-between text-xs font-semibold text-accent-dark border-t border-borderc pt-2 mt-2">
                      <span>Total</span><span>{money(grupo.total)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- centros de costo
function TabCentrosCosto({ centrosCosto, puedeEditar, recargar }: { centrosCosto: CentroCosto[]; puedeEditar: boolean; recargar: () => void }) {
  const [codigo, setCodigo] = useState("");
  const [nombre, setNombre] = useState("");
  const [error, setError] = useState<string | null>(null);

  const crear = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await api.post("/contabilidad/centros-costo", { codigo, nombre });
      setCodigo(""); setNombre(""); recargar();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudo crear el centro de costo.");
    }
  };

  return (
    <div className="card p-5">
      <table className="w-full mb-4">
        <thead><tr className="border-b border-borderc"><th className="table-th">Código</th><th className="table-th">Nombre</th><th className="table-th">Estado</th></tr></thead>
        <tbody>
          {centrosCosto.map((cc) => (
            <tr key={cc.id} className="border-b border-borderc last:border-0">
              <td className="table-td font-mono text-xs">{cc.codigo}</td>
              <td className="table-td">{cc.nombre}</td>
              <td className="table-td"><span className={`badge ${cc.activo ? "bg-accent-light text-accent-dark" : "bg-slate-100 text-slate-500"}`}>{cc.activo ? "activo" : "inactivo"}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
      {puedeEditar && (
        <form onSubmit={crear} className="flex gap-2 items-end">
          <div><label className="block text-xs font-semibold text-slate-600 mb-1">Código</label><input className="input-field" value={codigo} onChange={(e) => setCodigo(e.target.value)} required /></div>
          <div className="flex-1"><label className="block text-xs font-semibold text-slate-600 mb-1">Nombre</label><input className="input-field" value={nombre} onChange={(e) => setNombre(e.target.value)} required /></div>
          <button type="submit" className="btn-primary">+ Agregar</button>
        </form>
      )}
      {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700 mt-2">{error}</div>}
    </div>
  );
}

// ---------------------------------------------------------------- períodos
function TabPeriodos({ periodos, puedeCerrar, recargar }: { periodos: Periodo[]; puedeCerrar: boolean; recargar: () => void }) {
  const cerrar = async (id: number) => {
    try { await api.post(`/contabilidad/periodos/${id}/cerrar`, {}); recargar(); } catch { /* noop */ }
  };
  const MESES = ["", "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
  return (
    <div className="card overflow-hidden">
      <table className="w-full">
        <thead><tr className="border-b border-borderc"><th className="table-th">Período</th><th className="table-th">Estado</th><th className="table-th"></th></tr></thead>
        <tbody>
          {periodos.map((p) => (
            <tr key={p.id} className="border-b border-borderc last:border-0">
              <td className="table-td font-medium">{MESES[p.mes]} {p.anio}</td>
              <td className="table-td"><span className={`badge ${p.estado === "abierto" ? "bg-accent-light text-accent-dark" : "bg-slate-100 text-slate-500"}`}>{p.estado}</span></td>
              <td className="table-td text-right">
                {puedeCerrar && p.estado === "abierto" && <button className="text-red-600 hover:underline text-xs font-semibold" onClick={() => cerrar(p.id)}>Cerrar período</button>}
              </td>
            </tr>
          ))}
          {periodos.length === 0 && <tr><td colSpan={3} className="table-td text-center text-textmuted py-8">Aún no hay períodos (se crean automáticamente al contabilizar).</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------- cuentas predeterminadas
function TabCuentasPredeterminadas({ cuentas, puedeEditar }: { cuentas: Cuenta[]; puedeEditar: boolean }) {
  const [config, setConfig] = useState<any[]>([]);
  const [cargando, setCargando] = useState(true);

  const cargar = async () => {
    setCargando(true);
    try { setConfig(await api.get("/contabilidad/cuentas-predeterminadas")); } finally { setCargando(false); }
  };
  useEffect(() => { cargar(); }, []);

  const actualizar = async (concepto: string, cuentaId: string) => {
    if (!cuentaId) return;
    try { await api.put(`/contabilidad/cuentas-predeterminadas/${concepto}`, { cuentaId: Number(cuentaId) }); cargar(); } catch { /* noop */ }
  };

  const cuentasMovimiento = cuentas.filter((c) => c.aceptaMovimiento && c.activo);

  if (cargando) return <p className="text-sm text-textmuted">Cargando...</p>;
  return (
    <div className="card overflow-hidden">
      <table className="w-full">
        <thead><tr className="border-b border-borderc"><th className="table-th">Concepto</th><th className="table-th">Cuenta configurada</th>{puedeEditar && <th className="table-th">Cambiar a</th>}</tr></thead>
        <tbody>
          {config.map((c) => (
            <tr key={c.concepto} className="border-b border-borderc last:border-0">
              <td className="table-td">{c.etiqueta}</td>
              <td className="table-td">{c.codigo ? <span className="font-mono text-xs">{c.codigo} — {c.nombre}</span> : <span className="text-amber-600 text-xs">Sin configurar</span>}</td>
              {puedeEditar && (
                <td className="table-td">
                  <select className="input-field" defaultValue="" onChange={(e) => actualizar(c.concepto, e.target.value)}>
                    <option value="">Seleccionar cuenta...</option>
                    {cuentasMovimiento.map((cta) => <option key={cta.id} value={cta.id}>{cta.codigo} — {cta.nombre}</option>)}
                  </select>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
